'use client'
import React from 'react'
import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '../ui/button'
import { useTheme } from '@/providers/Theme'
import { cn } from '@/utilities/ui'
import { PaginationEllipsis } from '../ui/pagination'

const Pagination = ({
  count,
  pagesize,
  setPage,
  page,
  showPages = 4,
  hasPrevPage,
  hasNextPage,
}: {
  count: number
  pagesize: number
  setPage: any
  page: number
  showPages: number
  hasPrevPage: boolean
  hasNextPage: boolean
}) => {
  const { theme } = useTheme()
  const totalPages = Math.ceil(count / pagesize)
  const pages = (currentPage: any, last: any) => {
    var list: any = []
    var Lastcell = null

    for (var i = 1; i <= last; i++) {
      if (
        i === 1 ||
        (currentPage === 1 && i <= showPages) ||
        i === last ||
        (currentPage === last && i >= last - showPages) ||
        i === currentPage ||
        (!(i <= currentPage - Math.ceil(showPages / 2)) && i <= currentPage) ||
        (!(i >= currentPage + Math.floor(showPages / 2)) && i >= currentPage)
      ) {
        list.push(i)
        Lastcell = `${i}`
      } else {
        if (Lastcell !== '...') {
          list.push('...')
          Lastcell = '...'
        }
      }
    }
    return list?.map((item: any, index: any) =>
      item === '...' && list[index] - list[index] === 2 ? list[index] : item,
    )
  }
  
  const iconColor = theme === 'dark' ? '#fff' : '#000'

  return (
    <div className="flex flex-row items-center justify-center gap-x-2">
      {/* First page */}
      <Button
        variant="ghost"
        className={
          cn("w-10 h-10 p-0 border-gray-200 hover:bg-primary-100", 
            theme === 'dark' ? 'bg-card' 
            : '')}
        type="button"
        onClick={() => setPage(1)}
        disabled={page === 1}
      >
        <ChevronFirst size={16} color={iconColor} className="rtl:rotate-180" />
      </Button>

      <Button
        variant="ghost"
        className={
          cn("w-10 h-10 p-0 border-gray-200 hover:bg-primary-100", 
            theme === 'dark' ? 'bg-card' 
            : '')}
        type="button"
        onClick={() => {
          if (page > 1) setPage(page - 1)
        }}
        disabled={hasPrevPage ? false : true}
      >
        <ChevronLeft size={16} color={iconColor} className="rtl:rotate-180" />
      </Button>

      <div className="flex flex-row items-center justify-center gap-1">
        {pages(page, Math.ceil(count / pagesize)).map((pageCell: any, index: any) => (
          <Cell key={index} setPage={setPage} page={page} number={pageCell} />
        ))}
      </div>

      <Button
        variant="ghost"
        className={
          cn("w-10 h-10 p-0 border-gray-200 hover:bg-primary-100", 
            theme === 'dark' ? 'bg-card' 
            : '')}
        type="button"
        onClick={() => {
          if (page < Math.ceil(count / pagesize)) setPage(page + 1)
        }}
        disabled={hasNextPage ? false : true}
      >
        <ChevronRight size={16} color={iconColor} className="rtl:rotate-180" />
      </Button>

      {/* Last page */}
      <Button
        variant="ghost"
        className={
          cn("w-10 h-10 p-0 border-gray-200 hover:bg-primary-100", 
            theme === 'dark' ? 'bg-card' 
            : '')}
        type="button"
        onClick={() => setPage(totalPages)}
        disabled={page === totalPages}
      >
        <ChevronLast size={16} color={iconColor} className="rtl:rotate-180" />
      </Button>
    </div>
  )
}

export default Pagination

const Cell = ({ setPage, page, number }: any) => {
  const { theme } = useTheme()

  const buttonClass = cn(
    theme === 'dark'
      ? 'bg-card text-white hover:text-white'
      : ''
  )

  const activeClass = cn(
    buttonClass,
    theme === 'dark'
      ? 'bg-card hover:bg-gray-700 hover:text-white border-white text-white'
      : 'bg-white border border-black text-black'
  )

  if (number === '...') {
    return (
      <PaginationEllipsis className={cn(theme === 'dark' ? 'text-white' : '')} />
    )
  }

  return (
    <Button
      variant={page === number ? 'default' : 'ghost'}
      type="button"
      disabled={number === '...' || page === number ? true : false}
      onClick={() => {
        setPage(number)
      }}
      className={cn(
        page === number ? activeClass : buttonClass,
      )}
    >
      <span>{number}</span>
    </Button>
  )
}
